// @ts-strict-ignore
import React from "react";
import { Text } from "@hubble/web";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { mediaQuery } from "@hubble/web";
import TextLink from "../NextLink/TextLink";
import Markdown from "../Markdown";
import type { DevicesObject } from "utils/common";

interface TypographyStyles {
  font: DevicesObject;
  margin: DevicesObject;
  padding: DevicesObject;
  inset: DevicesObject;
  background: DevicesObject;
  maxWidth: DevicesObject;
  textAlign: DevicesObject;
}

const typographyStyles = (
  device: "mobile" | "tablet" | "desktop",
  {
    font,
    margin,
    padding,
    inset,
    background,
    maxWidth,
    textAlign
  }: TypographyStyles
) => css`
  font: ${font?.[device]};
  margin: ${margin?.[device]};
  padding: ${padding?.[device]};
  inset: ${inset?.[device]};
  max-width: ${maxWidth?.[device]};
  text-align: ${textAlign?.[device]};
  ${background &&
  `background:${background?.[device]};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`};
`;

const typographyObject = (args) => css`
  ${typographyStyles("mobile", args)}
  @media ${mediaQuery.tabletSmUp} {
    ${typographyStyles("tablet", args)}
  }
  @media ${mediaQuery.desktopLgUp} {
    ${typographyStyles("desktop", args)}
  }
`;

const StyledBody = styled(Text.Body)<{
  args: TypographyStyles;
}>`
  ${(args) => typographyObject(args)}
`;

const StyledDisplay = styled(Text.Display)<{
  args: TypographyStyles;
}>`
  ${(args) => typographyObject(args)}
`;
const StyledHeading = styled(Text.Heading)<{
  args: TypographyStyles;
}>`
  ${(args) => typographyObject(args)}
`;

export const MarkdownText = styled.span`
  margin: 0;
`;

const LinkText = (props) => (
  <MarkdownText>
    <TextLink {...props}></TextLink>
  </MarkdownText>
);

export const TextComponent = (props) => {
  const common = {
    size:
      props?.size === "xxs" && props?.variant !== "heading"
        ? "xs"
        : props?.size,
    // default marketing to true
    marketing: props?.marketingTypeset === false ? false : true
  };

  const getText = (props) => {
    switch (props?.variant) {
      case "Body":
        return (
          <StyledBody {...props} {...common}>
            {props.text}
            <Markdown
              config={{ p: MarkdownText as any, link: LinkText }}
              source={props.markdown}
            />
          </StyledBody>
        );
      case "Display":
        return (
          <StyledDisplay {...props} {...common}>
            {props.text}
            <Markdown
              config={{ p: MarkdownText as any, link: LinkText }}
              source={props.markdown}
            />
          </StyledDisplay>
        );
      case "Heading":
        return (
          <StyledHeading {...props} {...common}>
            {props.text}
            <Markdown
              config={{ p: MarkdownText as any, link: LinkText }}
              source={props.markdown}
            />
          </StyledHeading>
        );
      default:
        return (
          <StyledBody {...props} {...common}>
            {props.text}
            <Markdown
              config={{ p: MarkdownText as any, link: LinkText }}
              source={props.markdown}
            />
          </StyledBody>
        );
    }
  };
  return getText({ ...props });
};

export default function ContentfulText(props) {
  const { typography, ...rest } = props;
  return <TextComponent {...typography} {...rest} />;
}

export const ContentfulTextArray = (props) => {
  return props?.text?.map((text) => <ContentfulText {...text} />);
};
